import {Box, Button, Flex, Heading, Spacer, Spinner, Stack, Textarea} from '@chakra-ui/react';
import React, {useState, useEffect, useRef} from 'react';
import FlexCard from '../../components/General/FlexCard';
import {set, useForm} from 'react-hook-form';
import {CustomButton, CustomCheckbox, CustomInput, CustomSelect, DatePicker, SubmitButton} from '../../components/General/FormInputs';
import {useLocation, useNavigate} from 'react-router-dom';
import {SMaster, Arranger, Location, Report} from '../../service/Urls';
import {queryStringBuilder, post, get, postDelete, getFile} from '../../service/ApiService';
import ErrorHelper from '../../service/ErrorHelper';
import LoadingWheel from '../../components/General/LoadingWheel';
import ZipCityState from '../../components/General/ZipCityState';

import {getServiceTypeByCode} from '../../helpers/ServiceTypeDictionary';
import {useTitle} from '../../components/General/Layout';
import {getUserToken, isAdmin} from '../../helpers/localStorage';
import {LocationSelect} from '../../components/Location/LocationSelect';
import {ArrangerSelect} from '../../components/Arranger/ArrangerSelect';
import {Select} from '@chakra-ui/react';
import {FormLabel} from '@chakra-ui/react';
import {Input} from '@chakra-ui/react';

export const MasterRecordMaintenance = props => {
  const [title, setTitle] = useTitle('Master Maintenance');
  const location = useLocation();
  const navigate = useNavigate();
  const [record, setRecord] = useState({smClient: ''});
  const [checkboxes, setCheckboxes] = useState({smSurrecd: false, smMailret: false});
  const [locList, setLocList] = useState([]);
  const [arrList, setArrList] = useState([]);
  const [isSalesMan, setIsSalesMan] = useState(true);
  const [svcType, setSvcType] = useState({key: ''});
  const [pdfUrl, setPdfUrl] = useState('');

  // can be a boolean or the string of the exception message, if false we will show master maintenance page
  const [isException, setIsException] = useState(false);
  const [arId, setArId] = useState(0);
  const [loId, setLoId] = useState(0);
  const [secArId, setSecArId] = useState(0);
  const [toast, setToast] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [isPrintNewLogoLoading, setPrintNewLogoLoading] = useState(false);
  const [elements, setElements] = useState({locations: {}, arrangers: {}, secArrangers: {}});

  const isMounted = useRef(true);
  const linkRef = useRef(null);

  const {
    register,
    reset,
    control,
    setValue,
    handleSubmit,
    formState: {errors},
  } = useForm({defaultValues: [checkboxes]});

  useEffect(() => {
    if (isAdmin()) setIsSalesMan(false);
    loadData(location?.state?.id);

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (pdfUrl.length > 0) linkRef.current.click();
  }, [pdfUrl]);

  const loadData = async id => {
    const response = await get(SMaster.RECORD + '/' + id);

    // Show error message if we can't get the main user data
    const onError = () => navigate(-1);
    setToast({response: response, onError: onError});
    if (!response.error) {
      // Set the prepopulated data then reset the form to get it to load
      response.tsent = response.tsent?.substring(0, 10);
      response.smCondate = response.smCondate?.substring(0, 10);
      response.smMretdt = response.smMretdt?.substring(0, 10);
      const arrRes = await get(Arranger.LIST + '/' + response.nclientpk);
      const locRes = await get(Location.LIST + '/' + response.nclientpk);
      setCheckboxes({smSurrecd: response.smSurrecd, smMailret: response.smMailret});

      setAndSortArrays(arrRes, locRes);
      setArId(response.smArrid ? response.smArrid : 0);
      setLoId(response.smLoc ? response.smLoc : 0);
      setSecArId(response.smSecarr ? response.smSecarr : 0);

      if (response.smSurrecd == null) response.smSurrecd = false;
      if (response.smMailret == null) response.smMailret = false;
      if (response.smContno == null) response.smContno = '';

      // sets the state for exceptions if error message exists, Texception exists, and Tresolved doesn't exist (api doesn't send null values, so exists will do)
      if (response.hasOwnProperty('texception') && !response.hasOwnProperty('tresolved')) {
        response?.smExcrea != undefined && response?.smExcrea != '' ? setIsException(response.smExcrea) : setIsException(true);
      }

      // sets current service type to be shown in header and for when we navigate back to the search
      if (response.csvctype) {
        const svc = getServiceTypeByCode(response.csvctype);
        setSvcType(svc);
        const recordType = isException ? 'Exception' : 'Master';
        setTitle(recordType + ' Maintenance - ' + svc.key);
      }

      setRecord(response);
      reset(response);
      setLoading(false);
    }
  };

  // sorts the location and arranger lists by loId and arId then sets the state
  const setAndSortArrays = (arrRes, locRes) => {
    arrRes.data.push({arId: 0, arrangerName: 'None'});
    locRes.data.push({loId: 0, loName: 'None'});
    locRes.data.sort((a, b) => {
      if (a.loId > b.loId) return 1;
      else return -1;
    });
    arrRes.data.sort((a, b) => {
      if (a.arId > b.arId) return 1;
      else return -1;
    });
    setArrList(arrRes.data);
    setLocList(locRes.data);
  };

  const submitChange = async (data, goBack = True) => {
    data.locationpk = loId == 0 ? null : locList.find(loc => loc.loId == loId).npk;
    data.arrangerpk = arId == 0 ? null : arrList.find(ar => ar.arId == arId).npk;
    data.nsecarrpk = secArId == 0 ? null : arrList.find(ar => ar.arId == secArId).npk;
    data.smLoc = loId == 0 ? null : loId;
    data.smArrid = arId == 0 ? null : arId;
    data.smSecarr = secArId == 0 ? null : secArId;
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (data[key] == '' || data[key] == undefined) {
          data[key] = null;
        }
      }
    }
    const response = await post(SMaster.UPDATE + '/', JSON.stringify(data));

    const successMessage = 'Successfully updated registration number ' + record.smRecord;
    const onSuccess = () => (goBack ? navToSearch() : console.log('Saved'));
    setToast({response: response, successMessage: successMessage, onSuccess: onSuccess});
  };

  const handleMove = async () => {
    handleSubmit(async data => {
      await submitChange(data, false);

      const moveResponse = await post(SMaster.MOVE, '"' + data.npk + '"');
      setToast(moveResponse);
      if (!moveResponse.error) {
        navToSearch();
      }
    })();
  };

  const handleDelete = async () => {
    handleSubmit(async data => {
      const response = await postDelete(SMaster.DELETE, `"${data.npk}"`);
      setToast({response: response, successMessage: 'Successfully deleted record ' + data.smRecord});
      navToSearch();
    })();
  };

  const onPrintNewLogoClicked = async () => {
    const params = {
      smasterId: record.npk,
    };

    try {
      const url = queryStringBuilder(`${Report.GET_LETTER_SURVEY}`, params);

      setPrintNewLogoLoading(true);

      const response = await getFile(url);

      if (!isMounted.current) return;

      setPrintNewLogoLoading(false);

      if (response.error) {
        setToast({response: {error: 1}, errorMessage: 'Error getting report: ' + response.error});
        return;
      }

      const fileURL = window.URL.createObjectURL(response);

      if (!response.error) {
        setPdfUrl(fileURL);
      }
    } catch (error) {
      setPrintNewLogoLoading(false);
      setToast({response: {error: 1}, errorMessage: 'Error getting report: ' + error});
    }
  };

  const navToSearch = () => {
    if (isException) {
      // navigate('/maintenance/exception', {state: {serviceType: svcType}});
    } else {
      navigate('/maintenance/master', {state: {serviceType: svcType}});
    }
  };

  const SelectPicker = ({name, id, value, onChange, options}) => {
    return (
      <Flex w="100%">
        <Box w="16%">
          <FormLabel htmlFor={id} mb="-2" ml="1" fontSize="sm">
            {name + ' Id'}
          </FormLabel>
          <Input mt="6px" defaultValue={value} readOnly disabled />
        </Box>
        <Spacer />
        <Box w="83%">
          <FormLabel htmlFor={id} mb="-2" ml="1" fontSize="sm">
            {name}
          </FormLabel>
          <Select mt="6px" w="100%" value={value} id={id} onChange={onChange}>
            {options}
          </Select>
        </Box>
      </Flex>
    );
  };

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toast} />
        </>
      ) : (
        <Box mr="2">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" ref={linkRef} style={{display: 'none'}} />
          <form id="master" onSubmit={handleSubmit(submitChange)}>
            {isException && isException?.length > 0 && (
              <Box>
                <Heading color="red" size="md">
                  {isException.replace(/\|$/, '')}
                </Heading>
              </Box>
            )}
            <FlexCard>
              <Flex w={'100%'} justify="space-between">
                <Stack w="70%" justify="left" minW="200px" mr="5">
                  <Flex>
                    <CustomInput register={register} errors={errors} id="smClient" name="Client ID" isReadOnly={true} />
                    <CustomInput w="100%" register={register} errors={errors} id="clientName" name="Client" isReadOnly={true} />
                  </Flex>
                  <Flex>
                    <SelectPicker
                      name="Location"
                      id="locationId"
                      value={loId}
                      onChange={e => setLoId(e.target.value)}
                      options={locList.map(loc => {
                        return (
                          <option key={loc.npk + 'loc'} value={loc.loId}>
                            {loc.loName + ' | ' + loc.loRptname}
                          </option>
                        );
                      })}
                    />
                  </Flex>
                  <Flex>
                    <SelectPicker
                      name="Arranger"
                      id="arrangerId"
                      value={arId}
                      onChange={e => setArId(e.target.value)}
                      options={arrList.map(ar => {
                        return (
                          <option key={ar.npk + 'ar'} value={ar.arId}>
                            {ar.arrangerName}
                          </option>
                        );
                      })}
                    />
                  </Flex>
                  <Flex>
                    <SelectPicker
                      name="Secondary Arranger"
                      id="secArrangerId"
                      value={secArId}
                      onChange={e => setSecArId(e.target.value)}
                      options={arrList.map(ar => {
                        return (
                          <option key={ar.npk + 'secAr'} value={ar.arId}>
                            {ar.arrangerName}
                          </option>
                        );
                      })}
                    />
                  </Flex>
                </Stack>
                <Flex justify="space-evenly" flexDirection="column" w="20%">
                  <Heading size="md">Record Number: {record.smRecord}</Heading>
                  <CustomInput register={register} errors={errors} id="smBatch" name="Batch Number" isReadOnly={true} defaultValue={record.smBatch} />
                  <DatePicker
                    register={register}
                    errors={errors}
                    id="smCondate"
                    name="Import Date"
                    isReadOnly={true}
                    defaultValue={record.smCondate?.substring(0, 10)}
                  />
                  <CustomInput
                    register={register}
                    errors={errors}
                    id="smServid"
                    name="Contract Number"
                    isReadOnly={true}
                    // value={record.smServid}
                    // onChange={stateCorrector}
                  />
                </Flex>
              </Flex>
            </FlexCard>
            <Flex>
              <FlexCard w="50%" mr="1" mb="0">
                <Stack>
                  <Heading size="md">Buyer Information</Heading>
                  <Flex>
                    <CustomInput register={register} errors={errors} id="smBtitle" name="Title" />
                    <CustomInput register={register} errors={errors} id="smBfname" name="First Name" />
                    <CustomInput register={register} errors={errors} id="smBmname" name="Middle Initial" />
                  </Flex>
                  <Flex>
                    <CustomInput register={register} errors={errors} id="smBlname" name="Last Name" />
                    <CustomInput register={register} errors={errors} id="smBsuffix" name="Suffix" />
                  </Flex>
                  <Flex>
                    <CustomInput register={register} errors={errors} id="smBadd1" name="Address" />
                    <CustomInput register={register} errors={errors} id="smBadd2" name="Address2" />
                  </Flex>
                  <Flex>
                    <ZipCityState
                      setValue={setValue}
                      control={control}
                      register={register}
                      errors={errors}
                      values={{
                        zip: {id: 'smBzip', value: record.smBzip},
                        city: {id: 'smBcity', value: record.smBcity},
                        state: {id: 'smBstate', value: record.smBstate},
                      }}
                    />
                    {/* <CustomInput register={register} errors={errors} id="smBzip" name="ZipCode" />
                    <CustomInput register={register} errors={errors} id="smBcity" name="City" />
                    <CustomInput register={register} errors={errors} id="smBstate" name="State" /> */}
                  </Flex>
                  <Flex>
                    <CustomInput register={register} errors={errors} id="smHphone" name="Phone" />
                    <DatePicker register={register} errors={errors} id="smCondate" name="Contract Date" />
                    <CustomInput mx="2" register={register} errors={errors} id="smEmail" name="Email Address" isEmail />
                  </Flex>
                </Stack>
              </FlexCard>
              <Flex flexDirection="column" w="50%" ml="1" mb="2">
                <FlexCard pb="0">
                  <Stack>
                    <Heading size="md">Deceased Information</Heading>
                    <Flex>
                      <CustomInput register={register} errors={errors} id="smDtitle" name="Title" />
                      <CustomInput register={register} errors={errors} id="smDfname" name="First Name" />
                      <CustomInput register={register} errors={errors} id="smDmname" name="Middle Initial" />
                    </Flex>
                    <Flex>
                      <CustomInput register={register} errors={errors} id="smDlname" name="Last Name" />
                      <CustomInput register={register} errors={errors} id="smDsuffix" name="Suffix" />
                    </Flex>
                  </Stack>
                </FlexCard>
                <FlexCard mb="0">
                  <Flex>
                    <Spacer mx="1" />
                    <DatePicker id="tsent" name="Survey Sent" register={register} errors={errors} />
                    <Spacer mx="2" />
                    <CustomCheckbox
                      names="Survey Received"
                      id="smSurrecd"
                      // isChecked={checkboxes.smSurrecd}
                      control={control}
                      defaultValue={checkboxes.smSurrecd ? 1 : 0}
                    />
                  </Flex>
                </FlexCard>
                <FlexCard>
                  <Textarea rows={3} id="smComment" name="smComment" {...register('smComment')} errors={errors} />
                </FlexCard>
              </Flex>
            </Flex>

            {!isException && (
              <FlexCard>
                <Flex m="auto" alignItems="center">
                  <Box mx="2">
                    <CustomCheckbox
                      mx="2"
                      names="Returned by USPS"
                      id="smMailret"
                      // isChecked={checkboxes.smMailret}
                      control={control}
                      defaultValue={checkboxes.smMailret ? 1 : 0}
                    />
                  </Box>
                  <DatePicker mx="2" id="smMretdt" name="Date Returned" register={register} errors={errors} />
                  <CustomSelect mx="2" register={register} errors={errors} id="smReason" name="Reason">
                    {[
                      'No Such Number',
                      'No Such Street',
                      'Not Deliverable as Addressed',
                      'Forwarding Order Expired',
                      'Moved Left No Address',
                      'No Mail Receptable',
                      'Attempted - Not Known',
                      'Damaged in Transit',
                      'Vacant',
                      'Delivery Refused',
                      'Deceased',
                      'Unclaimed',
                      'Insufficient Address',
                      'Other',
                    ].map((value, index) => (
                      <option key={`${value}_${index}`} value={value}>
                        {value}
                      </option>
                    ))}
                  </CustomSelect>
                  <Box mx="2" mt="3">
                    <CustomButton name="Print" mt="1" disabled />
                  </Box>
                </Flex>
              </FlexCard>
            )}
            <Flex>
              <FlexCard w={isException ? '75%' : '50%'}>
                <Flex w="100%" m="auto">
                  {!isSalesMan && (
                    <>
                      <Box w={isException ? '25%' : '33%'} m="2">
                        <SubmitButton id="formButton" name="Save" />
                      </Box>
                      <Box w={isException ? '25%' : '33%'} m="2">
                        <CustomButton name="Delete" onClick={handleDelete} />
                      </Box>

                      <Box w="25%" m="2">
                        <CustomButton name="Move" onClick={handleMove} />
                      </Box>
                    </>
                  )}

                  <Box w={isException ? '25%' : '33%'} m="2">
                    <CustomButton name="Search" onClick={() => navToSearch()} />
                  </Box>
                </Flex>
              </FlexCard>
              {!isException && (
                <FlexCard w="50%">
                  <Box w="100%" m="auto">
                    <CustomButton
                      name={isPrintNewLogoLoading ? <Spinner /> : 'Print New Logo'}
                      onClick={onPrintNewLogoClicked}
                      disabled={isPrintNewLogoLoading}
                    />
                  </Box>
                </FlexCard>
              )}
            </Flex>
          </form>
          <ErrorHelper toastResponse={toast} />
        </Box>
      )}
    </>
  );
};
